import React from 'react';

/* Stroke icons for page content (section icons live in layout/NavIcon). They take the
   text color of their parent. Chevrons are named for RTL: "back" points right. */

export type IconName =
  | 'search' | 'close' | 'check' | 'alert' | 'refresh'
  | 'chevronBack' | 'chevronForward' | 'chevronDown' | 'chevronUp'
  | 'sortDesc' | 'sortAsc' | 'filter'
  | 'calendar' | 'clock' | 'document' | 'link' | 'external' | 'video'
  | 'play' | 'pause' | 'mic'
  | 'star' | 'starFilled' | 'share' | 'mail' | 'globe' | 'user' | 'users' | 'pin';

const PATHS: Record<IconName, React.ReactNode> = {
  search: (<><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></>),
  close: (<path d="M6 6l12 12M18 6L6 18" />),
  check: (<path d="M5 12.5l4.5 4.5L19 7.5" />),
  alert: (<><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5h.01" /></>),
  refresh: (<><path d="M20 12a8 8 0 1 1-2.34-5.66" /><path d="M20 4v4.5h-4.5" /></>),
  chevronBack: (<path d="M9 6l6 6-6 6" />),
  chevronForward: (<path d="M15 6l-6 6 6 6" />),
  chevronDown: (<path d="M6 9l6 6 6-6" />),
  chevronUp: (<path d="M6 15l6-6 6 6" />),
  sortDesc: (<path d="M12 5v14M6 13l6 6 6-6" />),
  sortAsc: (<path d="M12 19V5M6 11l6-6 6 6" />),
  filter: (<path d="M4 6h16M7 12h10M10 18h4" />),
  calendar: (<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>),
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  document: (<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>),
  link: (<><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" /><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" /></>),
  external: (<><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" /></>),
  video: (<><rect x="3" y="6" width="13" height="12" rx="2" /><path d="M16 10l5-3v10l-5-3" /></>),
  // Filled, like the media controls they sit next to; play points right in RTL too
  play: (<path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none" />),
  pause: (<><rect x="6.5" y="5" width="3.6" height="14" rx="1" fill="currentColor" stroke="none" /><rect x="13.9" y="5" width="3.6" height="14" rx="1" fill="currentColor" stroke="none" /></>),
  mic: (<><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></>),
  star: (<path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" />),
  starFilled: (<path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" fill="currentColor" />),
  share: (<path d="M12 3v12M7 8l5-5 5 5M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />),
  mail: (<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>),
  globe: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>),
  user: (<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  users: (<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14.5a5 5 0 0 1 5.5 5" /></>),
  pin: (<><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>),
};

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  style?: React.CSSProperties;
}

const Icon = ({ name, size = 20, strokeWidth = 1.8, style }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0, display: 'block', ...style }}
  >
    {PATHS[name]}
  </svg>
);

export default Icon;
